import { useQuery, useZero } from "@rocicorp/zero/react";
import { type ReactNode, useEffect, useState } from "react";
import { sharedMutators } from "../../shared/mutators";
import { queries } from "../../shared/queries";
import {
  WORKFLOW_NAME_MAX_LENGTH,
  workflowNameError,
} from "../../shared/workflowName";
import { useHasAccess } from "../hooks/useHasAccess";
import { type MoveTarget, useMoveTargets } from "../hooks/useMoveTargets";
import { useMoveWorkflow } from "../hooks/useMoveWorkflow";
import { useRecordLinkCopied } from "../hooks/useRecordLinkCopied";
import { useRecordNavigationClick } from "../hooks/useRecordNavigationClick";
import { useSessionTenantOrigin } from "../hooks/useSessionTenantOrigin";
import { useWorkflowLinkMenuItem } from "../hooks/useWorkflowLinkMenuItem";
import { spaceIcon } from "../lib/spaceIcon";
import { useMakeUrl, routes } from "../lib/urls";
import { writeToClipboard } from "../lib/writeToClipboard";
import { Button } from "./Button";
import { ConfirmModal } from "./ConfirmModal";
import { Dropdown } from "./Dropdown";
import { Icon } from "./Icon";
import { MoveWorkflowModal } from "./MoveWorkflowModal";
import { RenameModal } from "./RenameModal";
import { Tooltip } from "./Tooltip";
import { WorkflowSettings } from "./WorkflowSettings";

type Props = {
  isReadOnly?: boolean;
  workflowId: string;
  workflowName: string;
  spaceId: string;
  hasLiveVersion: boolean;
  workflowLinkHidden: boolean;
  workflowLinkCorner: string | null;
  opener?: ReactNode;
  // Drops the menu from this anchor’s left edge at its width, like the workflow’s feature menus.
  target?: string;
  afterDelete?: () => void;
};

function MoveTargetsLoader({
  onTargets,
}: {
  onTargets: (targets: MoveTarget[]) => void;
}) {
  const targets = useMoveTargets();
  useEffect(() => {
    onTargets(targets);
  }, [targets, onTargets]);
  return null;
}

export function WorkflowActionsMenu({
  isReadOnly = false,
  workflowId,
  workflowName,
  spaceId,
  hasLiveVersion,
  workflowLinkHidden,
  workflowLinkCorner,
  opener,
  target,
  afterDelete,
}: Props) {
  const makeUrl = useMakeUrl();
  const recordClick = useRecordNavigationClick();
  const recordLinkCopied = useRecordLinkCopied();
  const z = useZero();
  const hasWriteAccess = useHasAccess("space:write", spaceId);
  const canWrite = hasWriteAccess && !isReadOnly;
  const canPushLive = useHasAccess("space:push_live", spaceId);
  const canCreateSpace = useHasAccess("tenant:space_create");
  const canTakeOffline = canWrite && (!hasLiveVersion || canPushLive);
  const {
    requestMove,
    pending: movePending,
    canMove,
    isLive,
    ready: moveReady,
    confirmMove,
    cancelMove,
  } = useMoveWorkflow();
  const [menuOpen, setMenuOpen] = useState(false);
  const [moveTargets, setMoveTargets] = useState<MoveTarget[]>([]);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [retentionOpen, setRetentionOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  if (!canWrite) {
    if (renameOpen) setRenameOpen(false);
    if (retentionOpen) setRetentionOpen(false);
  }
  if (!canTakeOffline) {
    if (confirmDeleteOpen) setConfirmDeleteOpen(false);
    if (movePending) cancelMove();
  }
  const tenantOrigin = useSessionTenantOrigin();
  const [favorites] = useQuery(queries.userFavoriteWorkflowIds());
  const isFavorite = favorites.some(
    (favorite) => favorite.workflowId === workflowId
  );
  const workflowLinkItem = useWorkflowLinkMenuItem({
    workflowId,
    hidden: workflowLinkHidden,
    corner: workflowLinkCorner,
    disabled: !canWrite,
  });

  const workflowUrl = makeUrl(routes.space.workflow, { spaceId, workflowId });

  const availableTargets = moveTargets.filter((s) => s.id !== spaceId);
  const createSpaceItem = canCreateSpace
    ? [
        {
          type: "link" as const,
          left: <Icon name="plus" size={16} />,
          label: "Create space",
          to: makeUrl(routes.modal.createSpace, { workflowId }),
          onClick: () => recordClick("workflow_menu", "create_space"),
        },
      ]
    : [];
  const moveSubmenuItem = ({ disabled }: { disabled?: boolean }) => ({
    type: "submenu" as const,
    left: <Icon name="input" size={16} />,
    label: "Move",
    disabled,
    searchable: availableTargets.length >= 10,
    searchEmptyLabel: "No matching spaces",
    submenu: [
      {
        items:
          availableTargets.length > 0
            ? availableTargets.map((s) => ({
                type: "button" as const,
                key: s.id,
                disabled: s.disabled,
                searchText: s.name,
                left: <Icon name={spaceIcon(s.mode)} size={16} />,
                label: s.disabledReason ? (
                  <Tooltip content={s.disabledReason}>
                    <span>{s.name}</span>
                  </Tooltip>
                ) : (
                  s.name
                ),
                onClick: () =>
                  requestMove({
                    workflowId,
                    targetSpaceId: s.id,
                    targetSpaceName: s.name,
                  }),
              }))
            : createSpaceItem,
      },
    ],
  });

  const primaryItems = [
    {
      type: "button" as const,
      left: <Icon name="pencil" size={16} />,
      label: "Rename",
      disabled: !canWrite,
      onClick: () => setRenameOpen(true),
    },
    {
      type: "button" as const,
      left: <Icon name="link-2" size={16} />,
      label: "Copy link",
      onClick: () => {
        void writeToClipboard(tenantOrigin + workflowUrl).then(
          (ok) => ok && recordLinkCopied("workflow")
        );
      },
    },
    {
      type: "button" as const,
      left: <Icon name={isFavorite ? "heart-crack" : "heart"} size={16} />,
      label: isFavorite ? "Remove favorite" : "Favorite",
      onClick: () => {
        z.mutate(sharedMutators.toggleWorkflowFavorite({ workflowId }));
      },
    },
    {
      type: "button" as const,
      left: <Icon name="calendar" size={16} />,
      label: "Execution retention",
      disabled: !canWrite,
      onClick: () => {
        recordClick("workflow_menu", "execution_retention");
        setRetentionOpen(true);
      },
    },
    workflowLinkItem,
    moveSubmenuItem({ disabled: !canTakeOffline }),
  ];

  return (
    <>
      {menuOpen && <MoveTargetsLoader onTargets={setMoveTargets} />}
      <Dropdown
        opener={opener ?? <Button variant="secondary" icon="more" />}
        target={target}
        align={target ? "bottom-left" : undefined}
        minWidth={180}
        matchOpenerWidth={!!opener}
        margin={opener ? "4px 0" : 4}
        onOpenChange={setMenuOpen}
        sections={[
          { items: primaryItems },
          {
            items: [
              {
                type: "button" as const,
                left: <Icon name="trash" size={16} />,
                label: "Delete",
                disabled: !canTakeOffline,
                onClick: () => setConfirmDeleteOpen(true),
              },
            ],
          },
        ]}
      />
      {renameOpen && canWrite && (
        <RenameModal
          title="Rename workflow"
          inputLabel="Workflow name"
          initialName={workflowName}
          maxLength={WORKFLOW_NAME_MAX_LENGTH}
          validateName={workflowNameError}
          onClose={() => setRenameOpen(false)}
          onRename={(name) => {
            z.mutate(sharedMutators.renameWorkflow({ workflowId, name }));
          }}
        />
      )}
      {movePending && canTakeOffline && moveReady && (
        <MoveWorkflowModal
          workflowId={movePending.workflowId}
          targetSpaceId={movePending.targetSpaceId}
          targetSpaceName={movePending.targetSpaceName}
          canMove={canMove}
          isLive={isLive}
          onClose={cancelMove}
          onConfirm={confirmMove}
        />
      )}
      {retentionOpen && canWrite && (
        <WorkflowSettings
          workflowId={workflowId}
          onClose={() => setRetentionOpen(false)}
        />
      )}
      <ConfirmModal
        open={confirmDeleteOpen && canTakeOffline}
        onClose={() => setConfirmDeleteOpen(false)}
        onConfirm={() => {
          if (!canTakeOffline) return;
          z.mutate(sharedMutators.deleteWorkflow({ workflowId }));
          setConfirmDeleteOpen(false);
          afterDelete?.();
        }}
        title="Delete workflow"
        description="This workflow and all its steps will be deleted. You can recover it from the space menu for 30 days."
        confirmLabel="Delete workflow"
        destructive
      />
    </>
  );
}
